// gift-with-purchase.js
//
// Custom element for the cart-drawer gift-with-purchase promo. The element
// is server-rendered by snippets/gift-with-purchase.liquid into one of three
// states (below_threshold / unlocked / earned) and is naturally re-instantiated
// whenever the cart-drawer section re-renders (drawer interactions, PDP add,
// and the component's own add/remove all include cart-drawer in their sections
// list, so the parent #CartDrawer.innerHTML is replaced — destroying and
// recreating this element with fresh Liquid-computed state).
//
// Deliberately does NOT subscribe to PUB_SUB_EVENTS.cartUpdate — the section
// re-render machinery handles re-rendering, and subscribing would duplicate
// fetches and race with cart-drawer-items.onCartUpdate().
//
// See docs/plans/2026-05-22-feat-cart-drawer-gift-with-purchase-component-plan.md
// for the full state machine, data-attribute contract, and design rationale.

// Module-level dedupe for in-flight auto-removals — survives component remounts
// (an instance-level Set wouldn't). Used in Phase 4.
const pendingRemovals = new Set();

// bfcache restore handler. Refreshing the cart-icon-bubble is the most-visible
// signal — drawer contents will re-render on next open. Module-level + idempotent
// so multiple JS loads (theme editor section swaps, hot reload) don't accumulate.
if (!window.__gwpPageshowBound) {
  window.__gwpPageshowBound = true;
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    fetch('/?section_id=cart-icon-bubble')
      .then((r) => r.text())
      .then((html) => {
        const section = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
        const target = document.getElementById('cart-icon-bubble');
        if (section && target) target.innerHTML = section.innerHTML;
      })
      .catch(() => {});
  });
}

if (!customElements.get('gift-with-purchase')) {
  class GiftWithPurchase extends HTMLElement {
    constructor() {
      super();
      this.requestGen = 0;
      this.abortController = null;
      // Delegated click handler bound once in the constructor (constructor runs
      // once per element). Survives any subsequent internal markup changes.
      this.addEventListener('click', this.onClick.bind(this));
    }

    connectedCallback() {
      this.giftLineKey = this.dataset.giftLineKey || '';
      // Auto-remove when the cart has dropped below the threshold but the gift
      // line is still in the cart. The module-level `pendingRemovals` set
      // dedupes by line key so concurrent remounts don't fire duplicate removes.
      if (this.dataset.state === 'below_threshold' && this.giftLineKey) {
        this.removeGift();
      }
    }

    onClick(event) {
      const button = event.target.closest('[data-claim-variant]');
      if (!button) return;
      event.preventDefault();
      if (button.classList.contains('disabled') || button.classList.contains('loading')) return;
      this.addGift(button.dataset.claimVariant, button);
    }

    async addGift(variantId, button) {
      const gen = ++this.requestGen;
      this.setLoading(button, true);

      // Cancel any prior in-flight request so we never race two adds.
      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();

      try {
        const body = JSON.stringify({
          // _gwp marks this as our gift; the Liquid detector also requires
          // line_price == 0 (defense-in-depth against property spoofing).
          items: [{ id: variantId, quantity: 1, properties: { _gwp: 'true' } }],
          sections: this.getSectionsToRender().map((s) => s.section),
          sections_url: this.sectionsUrl(),
        });
        const response = await fetch(routes.cart_add_url, {
          ...fetchConfig('javascript'),
          body,
          signal: this.abortController.signal,
        });

        // Stale-write guards: discard the response if a newer request has
        // started, or if the element has been disconnected mid-fetch.
        if (gen !== this.requestGen) return;
        if (!this.isConnected) return;

        const parsed = await response.json();
        if (parsed.status) {
          // Shopify error response (e.g. 422 sold-out, quantity-rule violation)
          this.showError(parsed.description || parsed.message || 'Could not add the gift.');
          return;
        }

        this.announce('Free gift added to your cart.');
        this.renderContents(parsed.sections);

        // In the post-claim state the gift-with-purchase element no longer
        // renders (earned state is hidden). Nothing to focus, nothing else
        // to do here. Deliberately NOT publishing cartUpdate — would trigger
        // cart-drawer-items to clobber itself with main-cart-items markup
        // (pre-existing Dawn quirk). #CartDrawer and #cart-icon-bubble were
        // already updated via renderContents.
      } catch (e) {
        if (e.name === 'AbortError') return;
        this.showError((window.cartStrings && window.cartStrings.error) || 'Could not add the gift. Please try again.');
      } finally {
        if (this.isConnected) this.setLoading(button, false);
      }
    }

    async removeGift() {
      if (pendingRemovals.has(this.giftLineKey)) return;
      pendingRemovals.add(this.giftLineKey);

      try {
        const body = JSON.stringify({
          id: this.giftLineKey,
          quantity: 0,
          sections: this.getSectionsToRender().map((s) => s.section),
          sections_url: this.sectionsUrl(),
        });
        const response = await fetch(routes.cart_change_url, { ...fetchConfig(), body });
        const parsed = await response.json();
        if (parsed.errors) return; // silently fail; next cart action will re-evaluate

        // Compute new "$XX away" from the post-removal cart total so the SR
        // announcement explains both what happened and the resulting state.
        const thresholdCents = Number(this.dataset.thresholdCents) || 0;
        const remainingCents = Math.max(0, thresholdCents - parsed.total_price);
        const remainingDollars = Math.ceil(remainingCents / 100);
        this.announce(
          `Free gift was removed because your cart total dropped below $${this.dataset.thresholdDollars}. ` +
          `You are now $${remainingDollars} away from earning it back.`
        );

        this.renderContents(parsed.sections);
        // Intentionally do NOT move focus — auto-removal is an indirect side
        // effect of the user's quantity change. Stealing focus from their
        // quantity input would violate WCAG 3.2.2 (On Input).
        //
        // Also deliberately NOT publishing cartUpdate — cart-drawer-items
        // would clobber itself with main-cart-items markup. Both #CartDrawer
        // and #cart-icon-bubble were already updated via renderContents.
      } catch (e) {
        // silent — next cart action will re-evaluate via the fresh server render
      } finally {
        pendingRemovals.delete(this.giftLineKey);
      }
    }

    // Mirrors the pattern in cart.js:118-123 / cart-drawer.js:63-70.
    renderContents(sections) {
      if (!sections) return;
      this.getSectionsToRender().forEach((s) => {
        const target = document.getElementById(s.id);
        if (!target || !sections[s.section]) return;
        target.innerHTML = this.getSectionInnerHTML(sections[s.section], s.selector);
      });
    }

    // Verbatim from cart.js:168-172.
    getSectionInnerHTML(html, selector) {
      return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
    }

    getSectionsToRender() {
      return [
        { id: 'CartDrawer', section: 'cart-drawer', selector: '#CartDrawer' },
        { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
      ];
    }

    // Shopify won't render the cart-drawer snippet on a 404; fall back to /cart.
    sectionsUrl() {
      const path = window.location.pathname;
      return path.startsWith('/404') ? '/cart' : path;
    }

    // Debounce + clear-then-write. NVDA/JAWS skip identical successive writes;
    // clearing first guarantees the announcement on same-string updates.
    announce(message) {
      const region = this.querySelector('[data-announce]');
      if (!region) return;
      clearTimeout(this._announceTimeout);
      region.textContent = '';
      this._announceTimeout = setTimeout(() => {
        region.textContent = message;
      }, 50);
    }

    setLoading(button, loading) {
      if (!button) return;
      button.classList.toggle('loading', loading);
      button.setAttribute('aria-disabled', loading ? 'true' : 'false');
      const spinner = button.querySelector('.loading-overlay__spinner');
      if (spinner) spinner.classList.toggle('hidden', !loading);
    }

    showError(message) {
      const wrapper = this.querySelector('.gift-with-purchase__error-message-wrapper');
      const text = this.querySelector('.gift-with-purchase__error-message');
      if (text) text.textContent = message || '';
      if (wrapper) wrapper.toggleAttribute('hidden', !message);
    }
  }

  customElements.define('gift-with-purchase', GiftWithPurchase);
}
